import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { DefaultButton, PrimaryButton, Spinner, SpinnerSize, TextField } from "@fluentui/react";
import Title from "../../components/Title";

const GET_COVERAGE_CHAINS = gql`
  query ($country: String) {
    getCoverageChains(Country: $country) {
      ID Country Segment Name SortOrder
    }
  }
`;

const SET_COVERAGE_CHAIN = gql`
  mutation ($id: Int, $country: String, $segment: String, $name: String!) {
    setCoverageChain(input: { ID: $id, Country: $country, Segment: $segment, Name: $name }) {
      ID Country Segment Name SortOrder
    }
  }
`;

const DELETE_COVERAGE_CHAIN = gql`
  mutation ($id: Int!) {
    deleteCoverageChain(input: { ID: $id }) {
      Completed Error
    }
  }
`;

type Chain = { ID: number; Country: string; Segment: string; Name: string; SortOrder: number };

const COUNTRIES = ["ES", "PT", "IRL"];
const SEGMENTS: Record<string, string[]> = {
  ES:  ["QSR", "CASUAL", "CAFE"],
  PT:  ["QSR"],
  IRL: ["QSR"],
};
const SEGMENT_LABELS: Record<string, string> = {
  QSR: "QSR",
  CASUAL: "Casual Dining",
  CAFE: "Café",
};

const Cobertura = () => {
  const [country, setCountry] = useState("ES");
  const [newName, setNewName] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const { data, loading, refetch } = useQuery(GET_COVERAGE_CHAINS, {
    variables: { country },
    fetchPolicy: "no-cache",
  });

  const [addChain, { loading: adding }] = useMutation(SET_COVERAGE_CHAIN, {
    onCompleted: () => { setNewName({}); refetch(); },
  });

  const [editChain, { loading: saving }] = useMutation(SET_COVERAGE_CHAIN, {
    onCompleted: () => { setEditingId(null); setEditingName(""); refetch(); },
  });

  const [deleteChain] = useMutation(DELETE_COVERAGE_CHAIN, {
    onCompleted: () => refetch(),
  });

  const chains: Chain[] = data?.getCoverageChains ?? [];

  const bySegment = (seg: string) =>
    chains.filter(c => c.Segment === seg).sort((a, b) => a.Name.localeCompare(b.Name));

  const handleAdd = (segment: string) => {
    const name = (newName[segment] ?? "").trim();
    if (!name) return;
    addChain({ variables: { country, segment, name } });
  };

  return (
    <>
      <Title title="Mapa de cobertura" />

      {/* Tabs de país */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {COUNTRIES.map(c => (
          <DefaultButton
            key={c}
            text={c}
            primary={c === country}
            onClick={() => { setCountry(c); setNewName({}); }}
          />
        ))}
      </div>

      {loading && <Spinner size={SpinnerSize.medium} />}

      {!loading && (SEGMENTS[country] ?? ["QSR"]).map(seg => (
        <div key={seg} style={{ marginBottom: 32, maxWidth: 500 }}>
          <h3 style={{ marginBottom: 8 }}>{SEGMENT_LABELS[seg] ?? seg}</h3>

          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px 0" }}>
            {bySegment(seg).map(chain => (
              <li key={chain.ID} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: "1px solid #eee" }}>
                {editingId === chain.ID ? (
                  <>
                    <TextField
                      value={editingName}
                      onChange={(_, v) => setEditingName(v ?? "")}
                      onKeyDown={e => {
                        if (e.key === "Enter") editChain({ variables: { id: chain.ID, name: editingName.trim() } });
                        if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                      }}
                      styles={{ root: { flexGrow: 1 } }}
                      autoFocus
                    />
                    <PrimaryButton
                      text="Guardar"
                      disabled={saving || !editingName.trim()}
                      styles={{ root: { minWidth: 80, height: 28 } }}
                      onClick={() => editChain({ variables: { id: chain.ID, name: editingName.trim() } })}
                    />
                    <DefaultButton
                      text="Cancelar"
                      styles={{ root: { minWidth: 80, height: 28 } }}
                      onClick={() => { setEditingId(null); setEditingName(""); }}
                    />
                  </>
                ) : (
                  <>
                    <span style={{ flexGrow: 1 }}>{chain.Name}</span>
                    <DefaultButton
                      text="Editar"
                      styles={{ root: { minWidth: 70, height: 28 } }}
                      onClick={() => { setEditingId(chain.ID); setEditingName(chain.Name); }}
                    />
                    <DefaultButton
                      text="Eliminar"
                      styles={{ root: { minWidth: 80, height: 28 } }}
                      onClick={() => deleteChain({ variables: { id: chain.ID } })}
                    />
                  </>
                )}
              </li>
            ))}
            {bySegment(seg).length === 0 && (
              <li style={{ color: "#888", fontStyle: "italic" }}>Sin cadenas</li>
            )}
          </ul>

          <div style={{ display: "flex", gap: 8 }}>
            <TextField
              placeholder="Nueva cadena..."
              value={newName[seg] ?? ""}
              onChange={(_, v) => setNewName(prev => ({ ...prev, [seg]: v ?? "" }))}
              onKeyDown={e => { if (e.key === "Enter") handleAdd(seg); }}
              styles={{ root: { flexGrow: 1 } }}
            />
            <PrimaryButton
              text="Añadir"
              disabled={adding || !(newName[seg] ?? "").trim()}
              onClick={() => handleAdd(seg)}
            />
          </div>
        </div>
      ))}
    </>
  );
};

export default Cobertura;
